/*
Which brand the app wears.

A Good Thing has one sub-brand, "for sport", worn by orgs of the `sports`
kind (see `types/org_v1.ts`). It keeps the AGT heart but swaps the parent
brand's pinks for blue, and has its own lockup; everything else is shared.

Keep this the single place that decides, so a second sub-brand is a change
here rather than a hunt through components.
*/

import { derived, writable } from 'svelte/store';
import { appState } from './appState';

export type Brand = 'agt' | 'sport';

/*
The class put on <html> for the sub-brand. The pink -> blue overrides in
`styles/_brand_sport.scss` hang off it.

It has to appear as a literal in a file PurgeCSS scans (it reads the .svelte
and .ts files under src), or those overrides are stripped from the build as
unused. This is that file.
*/
export const SPORT_BRAND_CLASS = 'brand-sport';

export function brandForOrg(org: { kinds?: OrgKind[] } | undefined): Brand {
  return org?.kinds?.includes('sports') ? 'sport' : 'agt';
}

/*
The brand this device last signed in with, so signed-out surfaces (the
standard login, password reset, the page after signing out) keep a sports
club in its sub-brand rather than switching back to the parent brand. Set on
every sign-in, so whoever signed in last decides.

Browser storage can be unavailable (private windows, blocked site data); the
parent brand is the fallback.
*/
const REMEMBERED_BRAND_KEY = 'agt.brand';

function readRememberedBrand(): Brand {
  try {
    return window.localStorage.getItem(REMEMBERED_BRAND_KEY) === 'sport'
      ? 'sport'
      : 'agt';
  } catch {
    return 'agt';
  }
}

export const rememberedBrand = writable<Brand>(readRememberedBrand());

export function rememberBrand(value: Brand) {
  rememberedBrand.set(value);
  try {
    window.localStorage.setItem(REMEMBERED_BRAND_KEY, value);
  } catch {
    // not remembered beyond this page load
  }
}

/*
The brand to wear now: the signed-in org's, once it has loaded. A signed-in
user with no org (a business, a charity, an admin) wears the parent brand.
Signed out, or while an org user's org is still loading, the device's
remembered brand.

`org` is only trusted for a signed-in org user: appState keeps the last org
loaded until the /org layout clears it, so after signing out elsewhere (the
admin nav, another tab) it can be left over from someone else.
*/
export function currentBrand(
  user: { orgId?: string } | undefined,
  org: { kinds?: OrgKind[] } | undefined,
  remembered: Brand
): Brand {
  if (user?.orgId && org) return brandForOrg(org);
  if (user && !user.orgId) return 'agt';
  return remembered;
}

export const brand = derived(
  [appState, rememberedBrand],
  ([$appState, $remembered]) =>
    currentBrand($appState.user, $appState.org, $remembered)
);

// The horizontal lockup for the current brand, for use on a light background.
export const brandLogo = derived(brand, ($brand) =>
  $brand === 'sport' ? '/assets/img/logo_h_sport.png' : '/assets/img/logo_h.png'
);
